import { useEffect } from 'react';
import type { BoardDoc } from '../../doc/BoardDoc';
import { newId } from '../../doc/BoardDoc';
import type { Point } from '../../doc/geometry';
import type { SheetItem, SheetLayerDef } from '../../doc/schema';
import { enqueueUpload } from '../../sync/uploadQueue';
import type { Ctx } from '../actions';
import { getAssetBlob, storeCanvasAsset, storeImageAsset } from '../assets/localAssets';
import { downloadCanvas } from '../export/rasterize';
import type { PaneStore } from '../paneStore';
import { toolSettings } from '../toolSettings';
import { rasterStore, type LiveLayer } from './rasterStore';
import { sheetBoardSize, sheetSpec } from './presets';

/**
 * Paint mode: the sheet tool working on one sheet. The pane holds which sheet and layer; this
 * module turns that into live canvases (rasterStore), routes undo between pixels and the
 * board, and does the layer operations that need pixels (merge, flatten, export).
 */

export const isSheet = (item: { type: string } | undefined | null): item is SheetItem => item?.type === 'sheet';

/** Layers of a sheet, bottom to top. */
export function sheetLayerList(sheet: SheetItem): [string, SheetLayerDef][] {
  return Object.entries(sheet.props.layers).sort((a, b) => a[1].order - b[1].order);
}

/** The layer to paint on when none was chosen: the topmost visible, unlocked one, else the top one. */
export function defaultLayerFor(sheet: SheetItem): string | null {
  const list = sheetLayerList(sheet);
  for (let i = list.length - 1; i >= 0; i--) {
    const [id, l] = list[i]!;
    if (l.visible && !l.locked) return id;
  }
  return list[list.length - 1]?.[0] ?? null;
}

/** Puts the pane into paint mode on a sheet (selecting it). */
export function enterPaint(ctx: Ctx, sheetId: string, layerId?: string | null): void {
  const sheet = ctx.board.index.get(sheetId);
  if (!isSheet(sheet)) return;
  const s = ctx.pane.getState();
  const keep = s.paintSheetId === sheetId && s.paintLayerId && sheet.props.layers[s.paintLayerId] ? s.paintLayerId : null;
  const layer = layerId && sheet.props.layers[layerId] ? layerId : (keep ?? defaultLayerFor(sheet));
  s.select([sheetId]);
  s.setPaint(sheetId, layer);
}

/** Back to the select tool; the sheet stays selected. */
export function leavePaint(pane: PaneStore): void {
  pane.getState().setTool('select');
}

/** The sheet tool button or R: paint the selected sheet, or get ready to place a new one. */
export function sheetToolPressed(ctx: Ctx): void {
  const s = ctx.pane.getState();
  const [only] = s.selection;
  const item = only && s.selection.size === 1 ? ctx.board.index.get(only) : undefined;
  if (isSheet(item) && !ctx.board.isLocked(item)) enterPaint(ctx, item.id);
  else s.setTool('sheet');
}

/** A new sheet from the tool settings, centred on a world point, already in paint mode. */
export function createSheetAt(ctx: Ctx, world: Point): string {
  const t = toolSettings.getState();
  const spec = sheetSpec(t.sheetFormat, { dpi: t.sheetDpi, landscape: t.sheetLandscape, customW: t.sheetCustomW, customH: t.sheetCustomH });
  const { w, h } = sheetBoardSize(spec.pxW, spec.pxH, spec.dpi);
  const layerId = newId();
  const id = ctx.board.createItem({
    type: 'sheet',
    x: Math.round(world.x - w / 2),
    y: Math.round(world.y - h / 2),
    w,
    h,
    props: {
      pxW: spec.pxW,
      pxH: spec.pxH,
      dpi: spec.dpi,
      format: spec.format,
      paper: t.sheetPaper === 'none' ? null : t.sheetPaper,
      layers: { [layerId]: { name: 'Layer 1', order: 0, visible: true, locked: false, opacity: 1, assetId: null } },
    },
  });
  enterPaint(ctx, id, layerId);
  return id;
}

// ---- undo routing ---------------------------------------------------------------------------

function topAt(stack: { meta: Map<unknown, unknown> }[]): number {
  const top = stack[stack.length - 1];
  const at = top?.meta.get('at');
  return typeof at === 'number' ? at : 0;
}

/** Undo: the newer of the sheet's paint step and the board's step, while painting; else the board's. */
export async function undoAction(ctx: Ctx): Promise<void> {
  const { board, pane } = ctx;
  const sheetId = pane.getState().paintSheetId;
  const store = rasterStore(board);
  if (sheetId && store.canUndo(sheetId) && store.lastUndoAt(sheetId) >= topAt(board.undo.undoStack)) {
    await store.undo(sheetId);
    return;
  }
  board.undo.undo();
}

export async function redoAction(ctx: Ctx): Promise<void> {
  const { board, pane } = ctx;
  const sheetId = pane.getState().paintSheetId;
  const store = rasterStore(board);
  if (sheetId && store.canRedo(sheetId) && store.lastRedoAt(sheetId) >= topAt(board.undo.redoStack)) {
    await store.redo(sheetId);
    return;
  }
  board.undo.redo();
}

export function canUndoAction(ctx: Ctx): boolean {
  const sheetId = ctx.pane.getState().paintSheetId;
  return ctx.board.undo.canUndo() || (!!sheetId && rasterStore(ctx.board).canUndo(sheetId));
}

export function canRedoAction(ctx: Ctx): boolean {
  const sheetId = ctx.pane.getState().paintSheetId;
  return ctx.board.undo.canRedo() || (!!sheetId && rasterStore(ctx.board).canRedo(sheetId));
}

// ---- the session hook (one per pane) --------------------------------------------------------

/**
 * Keeps the raster store in step with the pane: the chosen layer becomes live, leaving paint
 * mode commits and retires, a deleted sheet or layer ends or re-targets the session, and a
 * hidden tab flushes pending pixels.
 */
export function usePaintSession(board: BoardDoc, pane: PaneStore, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    const store = rasterStore(board);
    const sync = () => {
      const s = pane.getState();
      if (s.tool !== 'sheet' || !s.paintSheetId) {
        store.deactivate();
        return;
      }
      const sheet = board.index.get(s.paintSheetId);
      if (!isSheet(sheet)) {
        s.setTool('select');
        return;
      }
      let layer = s.paintLayerId && sheet.props.layers[s.paintLayerId] ? s.paintLayerId : null;
      if (!layer) {
        layer = defaultLayerFor(sheet);
        s.setPaintLayer(layer);
        if (!layer) return;
      }
      void store.activate(sheet.id, layer);
    };
    const unsubPane = pane.subscribe((s, prev) => {
      if (s.tool !== prev.tool || s.paintSheetId !== prev.paintSheetId || s.paintLayerId !== prev.paintLayerId) sync();
    });
    const unsubDoc = board.index.subscribe(() => {
      const s = pane.getState();
      if (!s.paintSheetId) return;
      const sheet = board.index.get(s.paintSheetId);
      if (!isSheet(sheet) || (s.paintLayerId && !sheet.props.layers[s.paintLayerId])) sync();
    });
    const onVisibility = () => {
      if (document.hidden) void store.flush();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onVisibility);
    sync();
    return () => {
      unsubPane();
      unsubDoc();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onVisibility);
      store.deactivate();
    };
  }, [board, pane, enabled]);
}

// ---- pixel operations on whole layers -----------------------------------------------------

async function drawLayer(ctx: CanvasRenderingContext2D, live: LiveLayer | undefined, def: SheetLayerDef, w: number, h: number, alpha: number): Promise<void> {
  ctx.save();
  ctx.globalAlpha = alpha;
  if (live?.loaded) {
    ctx.drawImage(live.canvas, 0, 0, w, h);
  } else if (def.assetId) {
    const blob = await getAssetBlob(def.assetId, 'original');
    if (blob) {
      const bmp = await createImageBitmap(blob);
      ctx.drawImage(bmp, 0, 0, w, h);
      bmp.close();
    }
  }
  ctx.restore();
}

/** Every visible layer over the paper, at full pixel size. */
export async function compositeSheet(board: BoardDoc, sheet: SheetItem, opts: { paper?: boolean } = {}): Promise<HTMLCanvasElement> {
  const store = rasterStore(board);
  await store.flush();
  const canvas = document.createElement('canvas');
  canvas.width = sheet.props.pxW;
  canvas.height = sheet.props.pxH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  if (opts.paper !== false && sheet.props.paper) {
    ctx.fillStyle = sheet.props.paper;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  for (const [id, def] of sheetLayerList(sheet)) {
    if (!def.visible) continue;
    await drawLayer(ctx, store.liveLayer(sheet.id, id), def, canvas.width, canvas.height, def.opacity);
  }
  return canvas;
}

/** Flattens the sheet into an ordinary image item beside it (the sheet is left as it is). */
export async function flattenSheet(ctx: Ctx, sheetId: string): Promise<string | null> {
  const { board, pane } = ctx;
  const sheet = board.index.get(sheetId);
  if (!isSheet(sheet)) return null;
  const canvas = await compositeSheet(board, sheet);
  const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG encoding failed'))), 'image/png'));
  const maxSide = board.meta.get('importMaxSide');
  const meta = await storeImageAsset(new File([blob], 'sheet.png', { type: 'image/png' }), board.boardId, { maxSide: typeof maxSide === 'number' ? maxSide : 0 });
  const g = board.index.get(sheetId);
  if (!isSheet(g)) return null;
  const id = board.createItem({
    type: 'image',
    x: Math.round(g.x + g.w + 24),
    y: g.y,
    w: g.w,
    h: g.h,
    rotation: g.rotation,
    layerId: g.layerId,
    props: { assetId: meta.id, naturalW: meta.width, naturalH: meta.height, opacity: 1 },
  });
  if (board.remote) void enqueueUpload(board.boardId, meta.id);
  pane.getState().setTool('select');
  pane.getState().select([id]);
  return id;
}

export async function exportSheetPng(board: BoardDoc, sheetId: string, title: string): Promise<void> {
  const sheet = board.index.get(sheetId);
  if (!isSheet(sheet)) return;
  const canvas = await compositeSheet(board, sheet);
  await downloadCanvas(canvas, `${(title || board.boardId).replace(/[^\w.-]+/g, '_')}-sheet.png`);
}

/** Merges a layer into the one below it (the upper's opacity is baked in). */
export async function mergeLayerDown(board: BoardDoc, sheetId: string, layerId: string): Promise<void> {
  const store = rasterStore(board);
  await store.flush();
  const sheet = board.index.get(sheetId);
  if (!isSheet(sheet)) return;
  const list = sheetLayerList(sheet);
  const i = list.findIndex(([id]) => id === layerId);
  if (i <= 0) return;
  const [belowId, below] = list[i - 1]!;
  const upper = list[i]![1];
  const canvas = document.createElement('canvas');
  canvas.width = sheet.props.pxW;
  canvas.height = sheet.props.pxH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  await drawLayer(ctx, store.liveLayer(sheetId, belowId), below, canvas.width, canvas.height, 1);
  if (upper.visible) await drawLayer(ctx, store.liveLayer(sheetId, layerId), upper, canvas.width, canvas.height, upper.opacity);
  const meta = await storeCanvasAsset(canvas, board.boardId, 'merged.png');
  if (!board.index.has(sheetId)) return;
  store.clearHistory(sheetId);
  board.mergeSheetLayerDown(sheetId, layerId, meta.id);
  if (board.remote) void enqueueUpload(board.boardId, meta.id);
}

/** A copy of the layer right above it (the pixels are shared: assets never change). */
export async function duplicateLayer(board: BoardDoc, sheetId: string, layerId: string): Promise<string | null> {
  await rasterStore(board).flush();
  const def = isSheet(board.index.get(sheetId)) ? (board.index.get(sheetId) as SheetItem).props.layers[layerId] : undefined;
  if (!def) return null;
  return board.addSheetLayer(sheetId, { name: `${def.name} copy`, aboveId: layerId, assetId: def.assetId });
}

/** Empties a layer (undoable with the board's undo; its paint history is dropped). */
export async function clearLayer(board: BoardDoc, sheetId: string, layerId: string): Promise<void> {
  const store = rasterStore(board);
  await store.flush();
  store.clearHistory(sheetId, layerId);
  board.updateSheetLayer(sheetId, layerId, { assetId: null });
}

export async function deleteLayer(board: BoardDoc, sheetId: string, layerId: string): Promise<void> {
  const store = rasterStore(board);
  await store.flush();
  store.clearHistory(sheetId, layerId);
  board.deleteSheetLayer(sheetId, layerId);
}
